import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Button, Form, Badge, Table } from 'react-bootstrap';
import { ApiGet } from '../../../../ApiServices/ApiServices';
import { toast } from 'react-toastify';

const Dashboard = () => {
  const [dateRange, setDateRange] = useState({
    fromDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    toDate: new Date().toISOString().split('T')[0]
  });

  const [summaryData, setSummaryData] = useState({
    summary: {
      totalOrders: 0,
      totalSales: 0,
      averageOrderValue: 0,
      transactions: 0
    },
    orderByStatus: {
      PENDING: 0
    },
    salesTrend: [],
    topSellingItems: []
  });

  const [loading, setLoading] = useState({ summary: false });
  const [error, setError] = useState({ summary: '' });
  const [lastRefresh, setLastRefresh] = useState(new Date());

  useEffect(() => {
    fetchDashboardSummary();
  }, []);

  const fetchDashboardSummary = async () => {
    setLoading(prev => ({ ...prev, summary: true }));
    setError(prev => ({ ...prev, summary: '' }));

    try {
      const result = await ApiGet('/api/cashier/dashboard/summary', {
        fromDate: dateRange.fromDate,
        toDate: dateRange.toDate
      });

      if (result.success) {
        const apiData = result.success.data.data;
        setSummaryData({
          summary: {
            totalOrders: apiData.totalOrders || 0,
            totalSales: apiData.totalSales || apiData.totalRevenue || 0,
            averageOrderValue: apiData.averageOrderValue || 0,
            transactions: apiData.transactions || apiData.totalOrders || 0
          },
          orderByStatus: apiData.ordersByStatus || apiData.orderByStatus || { PENDING: 0 },
          salesTrend: apiData.salesTrend || apiData.revenueTrend || [],
          topSellingItems: apiData.topSellingItems || apiData.topMenuItems || []
        });
        setLastRefresh(new Date());
      } else {
        setError(prev => ({ ...prev, summary: result.fail }));
      }
    } catch (err) {
      setError(prev => ({ ...prev, summary: 'Failed to fetch dashboard data' }));
    } finally {
      setLoading(prev => ({ ...prev, summary: false }));
    }
  };

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilter = () => {
    fetchDashboardSummary();
  };

  const handleRefresh = () => {
    fetchDashboardSummary();
    toast.success('Dashboard refreshed successfully!');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const StatCard = ({ icon, title, value, subtitle, trend, trendValue, iconColor = "#d74440", loading = false, isCurrency = false }) => (
    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
      <Card.Body>
        {loading ? (
          <div className="text-center py-2">
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <div className="text-muted small mb-1">{title}</div>
              <div className="fs-3 fw-bold" style={{ color: '#d74440' }}>
                {isCurrency ? formatCurrency(value) : (value?.toLocaleString() || 0)}
              </div>
              {subtitle && (
                <div className="small text-muted mt-1">
                  {subtitle}
                  {trend && trendValue && (
                    <span className="ms-2 text-success">
                      <i className="bi bi-arrow-up"></i> {trendValue}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="p-2 rounded" style={{ backgroundColor: `${iconColor}15` }}>
              <i className={`${icon} fs-4`} style={{ color: iconColor }}></i>
            </div>
          </div>
        )}
      </Card.Body>
      <div style={{ height: '4px', backgroundColor: iconColor, borderRadius: '0 0 12px 12px' }}></div>
    </Card>
  );

  const getStatusBadgeColor = (status) => {
    const colors = {
      'PENDING': 'primary',
      'COMPLETED': 'success',
      'CANCELLED': 'danger'
    };
    return colors[status] || 'secondary';
  };

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0" style={{ color: '#d74440', fontWeight: '700' }}>
          <i className="bi bi-person-badge me-2"></i>
          Cashier Dashboard
        </h2>
        <div className="d-flex align-items-center gap-2">
          <Form.Control
            type="date"
            value={dateRange.fromDate}
            onChange={(e) => handleDateChange('fromDate', e.target.value)}
            style={{ width: '150px' }}
          />
          <span className="text-muted">to</span>
          <Form.Control
            type="date"
            value={dateRange.toDate}
            onChange={(e) => handleDateChange('toDate', e.target.value)}
            style={{ width: '150px' }}
          />
          <Button
            variant="outline-danger"
            onClick={handleApplyFilter}
            disabled={loading.summary}
          >
            Filter
          </Button>
        </div>
      </div>

      {error.summary && (
        <Alert variant="danger" className="mb-4">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error.summary}
        </Alert>
      )}

      <Row className="g-3 mb-4">
        <Col lg={3} md={6}>
          <StatCard
            icon="bi bi-currency-rupee"
            title="Total Sales"
            value={summaryData.summary.totalSales}
            subtitle="This period"
            trend={true}
            trendValue="+12.5%"
            iconColor="#d74440"
            loading={loading.summary}
            isCurrency={true}
          />
        </Col>
        <Col lg={3} md={6}>
          <StatCard
            icon="bi bi-bag-check"
            title="Total Orders"
            value={summaryData.summary.totalOrders}
            subtitle="This period"
            trend={true}
            trendValue="+8.2%"
            iconColor="#d74440"
            loading={loading.summary}
          />
        </Col>
        <Col lg={3} md={6}>
          <StatCard
            icon="bi bi-graph-up-arrow"
            title="Average Order Value"
            value={summaryData.summary.averageOrderValue}
            subtitle="This period"
            trend={true}
            trendValue="+5.7%"
            iconColor="#d74440"
            loading={loading.summary}
            isCurrency={true}
          />
        </Col>
        <Col lg={3} md={6}>
          <StatCard
            icon="bi bi-receipt"
            title="Transactions"
            value={summaryData.summary.transactions}
            subtitle="Processed"
            trend={true}
            trendValue="+3.2%"
            iconColor="#d74440"
            loading={loading.summary}
          />
        </Col>
      </Row>

      <Row className="g-4 mb-4">
        <Col lg={12}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
            <Card.Body>
              <h5 className="mb-3 fw-bold">ORDERS BY STATUS</h5>
              {loading.summary ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : (
                <Row className="g-3">
                  {Object.entries(summaryData.orderByStatus).map(([status, count]) => (
                    <Col key={status} md={2} sm={4} xs={6}>
                      <Card
                        className={`border-0 h-100 text-center`}
                        style={{
                          borderRadius: '12px',
                          background: status === 'PENDING' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#f8f9fa'
                        }}
                      >
                        <Card.Body className="py-4">
                          <div className={`fs-1 fw-bold ${status === 'PENDING' ? 'text-white' : `text-${getStatusBadgeColor(status)}`}`}>
                            {count}
                          </div>
                          <div className={`small ${status === 'PENDING' ? 'text-white' : 'text-muted'}`}>
                            {status.charAt(0) + status.slice(1).toLowerCase()}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-4">
        <Col lg={8}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
            <Card.Body>
              <h5 className="mb-3 fw-bold">SALES TREND (LAST 7 DAYS)</h5>
              {loading.summary ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : summaryData.salesTrend.length > 0 ? (
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Orders</th>
                        <th>Sales</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summaryData.salesTrend.map((item, index) => (
                        <tr key={index}>
                          <td>{new Date(item.date).toLocaleDateString()}</td>
                          <td><Badge bg="primary">{item.orders || 0}</Badge></td>
                          <td className="fw-bold text-success">{formatCurrency(item.sales || item.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-graph-up fs-1 d-block mb-2"></i>
                  No sales data available
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
            <Card.Body>
              <h5 className="mb-3 fw-bold">TOP SELLING ITEMS</h5>
              {loading.summary ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : summaryData.topSellingItems.length > 0 ? (
                <div className="d-flex flex-column gap-3">
                  {summaryData.topSellingItems.map((item, index) => (
                    <div key={index} className="d-flex justify-content-between align-items-center">
                      <div>
                        <div className="fw-semibold">{item.name || item.menuItemName}</div>
                        <small className="text-muted">{item.orderCount || item.count || 0} orders</small>
                      </div>
                      <span className="text-danger fw-bold">
                        {formatCurrency(item.revenue || item.totalRevenue || 0)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-basket fs-1 d-block mb-2"></i>
                  No data available
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;
